/**
 * storage.js — Firestore-backed drop-in for window.storage
 *
 * Mirrors the Claude artifact storage API so all existing window.storage
 * calls in App.js work without modification.
 *
 * Firestore structure:
 *   users/{userId}/cv_data/{key}  →  { value: string, updatedAt: Timestamp }  (default)
 *   cv_data/{key}                 →  { value: string, updatedAt: Timestamp }  (shared=true only)
 *
 * Non-shared keys belong to a specific user's subcollection — isolation is
 * structural (the path is users/{ownerId}/...), not regex-matched. By
 * default that's the signed-in caller (auth.currentUser.uid), but every
 * method also accepts an explicit `ownerId` 4th argument so an admin
 * *impersonating* another user can target that user's actual document tree
 * instead of their own.
 *
 * BUGFIX (see Cursivo-Bug-Report.docx, Bug 1): every call here used to
 * resolve to auth.currentUser.uid unconditionally, even when the caller was
 * an admin impersonating someone else. The impersonated userId was only
 * ever embedded as text inside the *key string* (e.g. "cv-pro-v3-USER_X-1"),
 * which this module silently ignored for the purposes of choosing WHICH
 * account's Firestore subtree to touch — so impersonation read/wrote the
 * admin's own account, not the target user's. Callers that need to act as
 * another user (CVEditor when `isImpersonating` is true) must now pass that
 * user's id explicitly as `ownerId`. Firestore rules must also grant the
 * caller access to that path — see Bug 2 in the same report.
 *
 * Calls made with shared=true (e.g. cv-share-* links, analytics-events-v2-*)
 * stay in the flat top-level collection since they're intentionally
 * cross-user. See firestore.rules.
 *
 * get() falls back to reading the flat collection if a key isn't found in
 * the subcollection yet (pre-migration data); set()/delete() always target
 * the subcollection going forward.
 */

import { db, auth } from "./firebase";
import {
  doc, getDoc, setDoc, deleteDoc,
  collection, getDocs, query, orderBy, startAt, endAt,
  serverTimestamp,
} from "firebase/firestore";

const COLLECTION = "cv_data";

// Resolves which user's subcollection a non-shared call targets. Defaults to
// the signed-in caller; pass `ownerId` explicitly to act on another user's
// data (impersonation — requires that user's Firestore rules to permit it).
// Throws a descriptive, non-"key not found" error when neither is available
// so callers relying on isStorageNotFound() (utils/logging.js) don't
// misclassify "not signed in yet" as "key doesn't exist" (see Bug 5).
function userDocRef(key, ownerId) {
  const uid = ownerId || auth.currentUser?.uid;
  if (!uid) throw new Error(`storage: no signed-in user and no ownerId provided — "${key}"`);
  return doc(db, "users", uid, "cv_data", key);
}

// High Unicode sentinel — Firestore sorts this after all normal characters,
// so startAt(prefix) + endAt(prefix + SENTINEL) gives a server-side prefix scan.
const SENTINEL = "\uFFFD";

const storage = {
  async get(key, shared = false, ownerId) {
    if (shared) {
      const snap = await getDoc(doc(db, COLLECTION, key));
      if (!snap.exists()) {
        throw new Error(`storage.get: key not found — "${key}"`);
      }
      return { key, value: snap.data().value, shared };
    }

    const snap = await getDoc(userDocRef(key, ownerId));
    if (snap.exists()) {
      return { key, value: snap.data().value, shared };
    }

    // Legacy bridge: data not yet migrated off the flat collection. Read-only —
    // the next set() for this key writes to the new subcollection path.
    const legacySnap = await getDoc(doc(db, COLLECTION, key));
    if (!legacySnap.exists()) {
      throw new Error(`storage.get: key not found — "${key}"`);
    }
    return { key, value: legacySnap.data().value, shared };
  },

  async set(key, value, shared = false, ownerId) {
    try {
      const ref = shared ? doc(db, COLLECTION, key) : userDocRef(key, ownerId);
      await setDoc(ref, {
        value,
        updatedAt: serverTimestamp(),
      });
      return { key, value, shared };
    } catch (e) {
      console.error("storage.set error:", e);
      return null;
    }
  },

  async delete(key, shared = false, ownerId) {
    try {
      const ref = shared ? doc(db, COLLECTION, key) : userDocRef(key, ownerId);
      await deleteDoc(ref);
      return { key, deleted: true, shared };
    } catch (e) {
      console.error("storage.delete error:", e);
      return null;
    }
  },

  async list(prefix = "", shared = false, ownerId) {
    try {
      // Route to the correct Firestore collection — same logic as get/set/delete.
      // Previously this always queried the flat top-level COLLECTION regardless
      // of the `shared` flag, returning empty or cross-user results for all
      // non-shared prefix scans (e.g. listing a user's own CV keys).
      const uid = ownerId || auth.currentUser?.uid;
      if (!shared && !uid) throw new Error(`storage.list: no signed-in user and no ownerId provided — prefix "${prefix}"`);
      const ref = shared
        ? collection(db, COLLECTION)
        : collection(db, "users", uid, "cv_data");
      // When a prefix is provided, use a lexicographic range query so Firestore
      // only reads the matching documents instead of scanning the entire collection.
      const snap = prefix
        ? await getDocs(query(ref, orderBy("__name__"), startAt(prefix), endAt(prefix + SENTINEL)))
        : await getDocs(ref);
      const keys = snap.docs.map(d => d.id);
      return { keys, prefix, shared };
    } catch (e) {
      console.error("storage.list error:", e);
      return null;
    }
  },
};

// Install once — does not overwrite if the Claude artifact API is already present.
const initStorage = () => {
  if (!window.storage) {
    window.storage = storage;
  }
};

// Execute immediately on first module load so React effects can safely call
// window.storage before DOMContentLoaded fires.
if (typeof window !== 'undefined') {
  initStorage();
}

export default storage;
